"use client";

import { forwardRef } from "react";
import shared, { paddingBlockStartEnd, paddingInlineStartEnd } from "./shared";
import { blue10, blue15, blue20, blue25, blue40, white, whiteTransparency30, text120, accentColorPrimary, setAppearanceConstantLocal } from "@churchofjesuschrist/eden-style-constants";
import styled from "styled-components";
import { adjustHexAlpha } from "@churchofjesuschrist/eden-utilities";
import { jsx as _jsx } from "react/jsx-runtime";
const [buttonSecondaryBackgroundDef, buttonSecondaryBackground] = setAppearanceConstantLocal("button-secondary-background", {
  light: white,
  dark: adjustHexAlpha(blue15, 0.08),
  prefix: "eden"
});
const [buttonSecondaryHoverDef, buttonSecondaryHover] = setAppearanceConstantLocal("button-secondary-hover", {
  light: blue25,
  dark: blue10,
  prefix: "eden"
});
const [buttonSecondaryHoverBackgroundDef, buttonSecondaryHoverBackground] = setAppearanceConstantLocal("button-secondary-hover-background", {
  light: white,
  dark: adjustHexAlpha(blue15, 0.16),
  prefix: "eden"
});
const [buttonSecondaryActiveDef, buttonSecondaryActive] = setAppearanceConstantLocal("button-secondary-active", {
  light: white,
  dark: blue20,
  prefix: "eden"
});
const [buttonSecondaryActiveBorderDef, buttonSecondaryActiveBorder] = setAppearanceConstantLocal("button-secondary-active-border", {
  light: blue40,
  dark: blue20,
  prefix: "eden"
});
const [buttonSecondaryActiveBackgroundDef, buttonSecondaryActiveBackground] = setAppearanceConstantLocal("button-secondary-active-background", {
  light: blue40,
  dark: "transparent",
  prefix: "eden"
});
const [buttonDisabledDef, buttonDisabled] = setAppearanceConstantLocal("button-disabled-color", {
  light: adjustHexAlpha(text120, 0.38),
  dark: whiteTransparency30,
  prefix: "eden"
});
const Component = styled.button.withConfig({
  componentId: "sc-11cmx9o-0"
})(["", ";", " ", " ", " ", " ", " ", " ", " padding:calc(", " - 1px) calc(", " - 1px);background-color:", ";color:", ";border:1px solid ", ";@media (any-hover:hover){&:hover{border-width:2px;padding:calc(", " - 2px) calc(", " - 2px);color:", ";border-color:", ";background-color:", ";}}&:active,&[aria-pressed=\"true\"]{background-color:", ";color:", ";border-color:", ";}&[disabled],&[disabled]:hover{padding:calc(", " - 1px) calc(", " - 1px);background:transparent;color:", ";border:1px solid ", ";}&:is(a):visited:not(:active){color:", ";}"], shared, buttonSecondaryBackgroundDef, buttonSecondaryHoverDef, buttonSecondaryHoverBackgroundDef, buttonSecondaryActiveDef, buttonSecondaryActiveBorderDef, buttonSecondaryActiveBackgroundDef, buttonDisabledDef, paddingBlockStartEnd, paddingInlineStartEnd, buttonSecondaryBackground, accentColorPrimary, accentColorPrimary, paddingBlockStartEnd, paddingInlineStartEnd, buttonSecondaryHover, buttonSecondaryHover, buttonSecondaryHoverBackground, buttonSecondaryActiveBackground, buttonSecondaryActive, buttonSecondaryActiveBorder, paddingBlockStartEnd, paddingInlineStartEnd, buttonDisabled, buttonDisabled, accentColorPrimary);

/**
 * On components with multiple buttons this should be the default style used. This button style indicates that an action is a viable path to choose when completing a task. This style is subservient to the `Primary` button style.
 * @param {object} props - The component props
 * @param {React.ReactNode} props.children - The button contents.
 * @param {boolean} [props.disabled] - If the button should be disabled.
 * @param {boolean} [props.fullWidth] - If the button should expand to the full width of its parent.
 * @param {string} [props.href] - The link that the button should navigate to on click.
 * @param {React.ElementType} [props.renderAs] - The HTML tag string or React component to render as.
 * @param {boolean} [props.small] - Whether to apply smaller styles.
 * @param {string} [props.type] - The [type of the button](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#Attributes) or the [type of the link](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#Attributes).
 * @param {boolean} [props."aria-pressed"] - When present, the button can act as a "toggle" button. The value indicates whether it is toggled on or not. Control this with your application state.
 * @param {React.Ref} ref - Ref forwarded to the underlying button element.
 * @returns {React.ReactElement} The Secondary component
 */
const SecondaryComponent = ({
  href,
  disabled,
  renderAs = href && !disabled ? "a" : "button",
  type = renderAs === "button" ? "button" : undefined,
  small,
  fullWidth,
  ...props
}, ref) => /*#__PURE__*/_jsx(Component, {
  ...props,
  ref: ref,
  type: type,
  href: href,
  disabled: disabled,
  $fullWidth: fullWidth,
  $small: small,
  as: renderAs,
  children: props.children
});
const Secondary = /*#__PURE__*/forwardRef(SecondaryComponent);
Secondary.displayName = "Secondary";
export default Secondary;